import {
  AnalyticsEvent,
  LaunchPinwheelFlowAction,
  PinwheelExperienceInput,
  Query,
  SdScrollableScreen,
  Typenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import formatDirectDepositBenefitsContent, {
  DirectDepositBenefitsContent,
  DirectDepositBenefitsDependencies,
} from 'features/directDepositBenefits/DirectDepositBenefitsScreen/formatDirectDepositBenefitsContent/formatDirectDepositBenefitsContent';
import { useOpenUrlDocumentScreen } from 'features/urlDocumentScreens/hooks/useOpenUrlDocumentScreen';
import { useCallback, useMemo, useState } from 'react';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import FetchDirectDepositBenefits from 'services/graphql/queries/FetchDirectDepositBenefits';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useFormattedCombinedOrCoreError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import useDirectDepositLoadingComponent from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import {
  genericRouteAction,
  UnknownGQLType,
} from 'shared/legacySDUIAdapters/genericRouteAction';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { Maybe } from 'shared/utils/types';
import { useQuery, UseQueryExecute } from 'urql';

interface UseDirectDepositBenefitsDataOptions {
  isOnboardingFlow?: boolean;
  onPinwheelSuccess?: () => void | Promise<void>;
}

export interface DirectDepositBenefitsData {
  fetching: boolean;
  refetch: UseQueryExecute;
  error?: Maybe<{ title: string; message: string }>;
  directDepositBenefitsContent?: Maybe<DirectDepositBenefitsContent>;
  handlePinwheelInitiation?: (actionId: string) => void;
  dismissDirectDepositIntro?: () => void;
  isCompletingOnboardingFlow?: boolean;
}

export const useDirectDepositBenefitsData = ({
  isOnboardingFlow = false,
  onPinwheelSuccess,
}: UseDirectDepositBenefitsDataOptions = {}): DirectDepositBenefitsData => {
  // NOTE: this screen is using a query that pre-dates cruise and is not aligned
  // with our new component system. for this reason the response is heavily
  // adapted on the client side to fit our requirements. new features to this
  // screen should be implemented using a new query that matches the new components.
  const [response, refetch] = useQuery<Query>({
    query: FetchDirectDepositBenefits,
  });
  const { data, error, fetching } = response;

  const [isCompletingOnboardingFlow, setIsCompletingOnboardingFlow] =
    useState(false);
  const {
    refetchDdManagementFlow,
    fetchPinWheelFlowPageData,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow({
    pause: true,
  });
  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  const { directDepositBenefits } = data ?? {};
  const router = useRouter();
  const { segmentClient } = useAnalyticsConfiguration();
  const { openPdfDocumentScreen } = useOpenUrlDocumentScreen();
  const { initiatePinwheel } = usePinwheel();

  const submitLogEvent = useCallback(
    (analyticsEvent: AnalyticsEvent) => {
      const { name, properties } = parseGQLAnalyticsEvent(analyticsEvent);
      void segmentClient.logEvent(name, properties);
    },
    [segmentClient],
  );

  const pinwheelDirectDepositBenefitLaunched = usePressableAnalytics({
    analyticsId: 'directDepositSwitchInitiatedDepositTracker',
  });

  const continueOnboardingFlow = useCallback(async () => {
    setIsCompletingOnboardingFlow(true);
    void fetchPinWheelFlowPageData('network-only');

    try {
      await onPinwheelSuccess?.();
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }

      logErrorWithContext(error, {
        featureArea: FeatureArea.DIRECT_DEPOSIT,
        responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
        severity: ErrorSeverity.LOW,
        additionalContext: {
          source: 'useDirectDepositBenefitsData.continueOnboardingFlow',
        },
      });
    } finally {
      setIsCompletingOnboardingFlow(false);
    }
  }, [fetchPinWheelFlowPageData, onPinwheelSuccess]);

  const dependencies: DirectDepositBenefitsDependencies = useMemo(() => {
    return {
      navigateForAction: (action: Maybe<UnknownGQLType>) => {
        if (!action) {
          return;
        }
        const routeAction = genericRouteAction(action);
        if (action?.__typename === Typenames.LaunchPinwheelFlowAction) {
          const launchPinwheelFlow = action as LaunchPinwheelFlowAction;
          if (launchPinwheelFlow.analyticsEvent) {
            submitLogEvent(launchPinwheelFlow.analyticsEvent);
          }
          if (launchPinwheelFlow.shouldLaunchPinwheelSdk) {
            const handleInitiatePinwheel = () => {
              initiatePinwheel({
                input: {
                  experience: PinwheelExperienceInput.DirectDepositSwitch,
                  skipIntroScreen,
                },
                launchBehavior: skipIntroScreen
                  ? { delayAfterTokenMs: 3000 }
                  : undefined,
                onOutcome: (result) => {
                  if (!isOnboardingFlow) {
                    void refetchDdManagementFlow(result);
                  }
                  if (result.success) {
                    if (isOnboardingFlow) {
                      continueOnboardingFlow();
                    }
                  }
                },
                onLinkTokenError: handleDdManagementLinkTokenFailure,
              });
              pinwheelDirectDepositBenefitLaunched();
            };
            handleInitiatePinwheel();
          } else {
            router.push('/move-money/direct-deposit/direct-deposit-management');
          }
        }

        if (routeAction) {
          const { analyticsEvent } = routeAction;
          if (analyticsEvent) {
            submitLogEvent(analyticsEvent);
          }

          if (
            action.__typename === Typenames.OpenPdfUrlAction &&
            'url' in action &&
            typeof action.url === 'string'
          ) {
            openPdfDocumentScreen(action.url);
          }
        }
      },
    };
  }, [
    initiatePinwheel,
    pinwheelDirectDepositBenefitLaunched,
    router,
    handleDdManagementLinkTokenFailure,
    refetchDdManagementFlow,
    submitLogEvent,
    openPdfDocumentScreen,
    skipIntroScreen,
    isOnboardingFlow,
    continueOnboardingFlow,
  ]);

  const formattedResponse = useMemo(() => {
    if (!directDepositBenefits || !directDepositBenefits?.__typename) {
      return null;
    }

    if (
      directDepositBenefits.__typename === Typenames.DirectDepositBenefitsError
    ) {
      return { error: directDepositBenefits };
    }

    if (
      directDepositBenefits.__typename ===
      Typenames.SDScrollableScreen.toString()
    ) {
      return {
        directDepositBenefitsContent: formatDirectDepositBenefitsContent(
          directDepositBenefits as SdScrollableScreen,
          dependencies,
        ),
      };
    }
  }, [dependencies, directDepositBenefits]);

  const formattedError = useFormattedCombinedOrCoreError(
    error ?? formattedResponse?.error,
  );

  return {
    fetching,
    error: formattedError,
    ...formattedResponse,
    refetch,
    isCompletingOnboardingFlow,
  };
};

export default useDirectDepositBenefitsData;
